import { z } from 'zod';

import { BRAZIL_TIMEZONE_IDS } from '../timezones.js';
import {
  MAX_UPLOAD_LIMIT_BYTES,
  MIN_UPLOAD_LIMIT_BYTES,
  type UploadPolicyMode,
} from '../uploads.js';

/**
 * Limites do intervalo de visita.
 *
 * O piso é um dia porque zero desligaria a regra de prazo sem dizer isso a
 * ninguém — uma obra sem prazo apareceria como "em dia" para sempre. O teto de
 * um ano é o ponto em que "de quanto em quanto tempo alguém vai lá" deixa de
 * ser fiscalização.
 */
export const MIN_VISIT_INTERVAL_DAYS = 1;
export const MAX_VISIT_INTERVAL_DAYS = 365;

/** Aceita `.PDF`, ` pdf ` e `PDF`; devolve sempre `pdf`. */
const extensionSchema = z
  .string()
  .trim()
  .toLowerCase()
  .transform((valor) => valor.replace(/^\.+/, ''))
  .pipe(
    z
      .string()
      .min(1, 'Extensão vazia')
      .max(12, 'Extensão longa demais')
      .regex(/^[a-z0-9]+$/, 'Use apenas letras e números'),
  );

export const updateSettingsBodySchema = z.object({
  defaultVisitIntervalDays: z
    .number()
    .int('Informe um número inteiro de dias')
    .min(MIN_VISIT_INTERVAL_DAYS, `O intervalo mínimo é de ${MIN_VISIT_INTERVAL_DAYS} dia`)
    .max(MAX_VISIT_INTERVAL_DAYS, `O intervalo máximo é de ${MAX_VISIT_INTERVAL_DAYS} dias`)
    .optional(),
  /*
   * Só os fusos do Brasil. Aceitar qualquer identificador do IANA abriria a
   * porta para um erro de digitação virar data errada em toda a tela, e o
   * sistema atende prefeituras brasileiras.
   */
  timezone: z.enum(BRAZIL_TIMEZONE_IDS as [string, ...string[]]).optional(),

  uploadPolicy: z.enum(['padrao', 'lista_branca']).optional(),
  /*
   * A extensão chega como a pessoa digitou — com ponto, em maiúscula, com
   * espaço em volta — e sai normalizada. Recusar ".PDF" por causa do ponto
   * seria transformar a tela num jogo de adivinhação.
   */
  uploadExtras: z.array(extensionSchema).max(200).optional(),
  uploadBlocked: z.array(extensionSchema).max(200).optional(),
  maxUploadBytes: z
    .number()
    .int()
    .min(MIN_UPLOAD_LIMIT_BYTES, 'O limite mínimo é de 1 MB')
    .max(MAX_UPLOAD_LIMIT_BYTES, 'O limite máximo é de 100 MB')
    .optional(),
});
export type UpdateSettingsBody = z.infer<typeof updateSettingsBodySchema>;

export interface OrganizationSettingsEntry {
  readonly id: string;
  readonly name: string;
  readonly slug: string;
  readonly cnpj: string | null;
  readonly defaultVisitIntervalDays: number;
  readonly timezone: string;
  /** Quantas obras já definem um intervalo próprio e ignoram o padrão. */
  readonly obrasWithOwnInterval: number;

  readonly uploadPolicy: UploadPolicyMode;
  readonly uploadExtras: readonly string[];
  readonly uploadBlocked: readonly string[];
  readonly maxUploadBytes: number;
  /**
   * As extensões que valem de fato, depois de aplicar modo, acréscimos,
   * bloqueios e o piso do sistema. É o que a tela mostra — a conta já feita, e
   * não os ingredientes dela.
   */
  readonly allowedExtensions: readonly string[];
}
